import { useCallback, useEffect, useReducer, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CrudMode } from '@ohpen/enso-ui';
import { IconAddressBook } from '@tabler/icons-react';

import { usePmpFetch } from '@pmp/hooks';
import { useValueListsStore } from '@pmp/stores';
import {
  ConfirmCrDeleteModal,
  ProcurationFeeAssignmentsCrudForm,
  ProcurationFeeAssignmentsList,
  PROCURATION_FEES_LIST_NAMES,
  procurationFeeAssignmentsColumns,
} from '@pmp/components';
import {
  ActionEntityType,
  firmProcFeeAssignmentsCreateDeleteCr,
  firmProcFeeAssignmentsValidateDelete,
  ActionType,
  DraftFirmProcFeeAssignment,
  firmProcFeeAssignmentsCreateChangeRequest,
  firmProcFeeAssignmentsCreateFirmReorderChangeRequest,
  FirmProcFeeAssignmentItemsResponse,
} from '@pmp/api';
import { crudToActionMap } from '@pmp/utils';

import { TabContent } from './TabContent';
import { CrudFormCard } from './CrudFormCard';
import { TabProps } from '../interfaces';

import { getInitialState, reducer } from '../../lender/reducer';

export const ProcurationFeesTab: React.FC<TabProps> = ({ form, mode, selectsOptions, }) => {
  const [isFetching, , data, , fetchData] = usePmpFetch('firmProcFeeAssignmentsGetAllFirmAssignments');
  const { valueLists, getValueLists } = useValueListsStore();
  const [state, dispatch] = useReducer(reducer<FirmProcFeeAssignmentItemsResponse>, getInitialState<FirmProcFeeAssignmentItemsResponse>());
  const { t } = useTranslation();
  const readonly = mode === CrudMode.VIEW;
  const firmId = form?.getFieldValue?.('firmId') as string;

  const refreshTable = useCallback(() => {
    fetchData({ path: { firmId } });
  }, [fetchData, firmId]);

  useEffect(() => {
    getValueLists({ alwaysFetch: PROCURATION_FEES_LIST_NAMES });
  }, [getValueLists]);

  useEffect(() => {
    refreshTable();
  }, [refreshTable]);

  const onCreate = useCallback(() => {
    dispatch({ type: 'openCrud', record: null, mode: CrudMode.CREATE });
  }, []);

  const onView = useCallback((record: FirmProcFeeAssignmentItemsResponse) => {
    dispatch({ type: 'openCrud', record, mode: CrudMode.VIEW });
  }, []);

  const onEdit = useCallback((record: FirmProcFeeAssignmentItemsResponse) => {
    dispatch({ type: 'openCrud', record, mode: CrudMode.EDIT });
  }, []);

  const onCloseCrud = useCallback(() => {
    dispatch({ type: 'closeCrud' });
  }, []);

  const onDelete = useCallback((record: FirmProcFeeAssignmentItemsResponse) => {
    dispatch({ type: 'openDeleteModal', record });
  }, []);

  const onCloseDeleteModal = useCallback(() => {
    dispatch({ type: 'closeDeleteModal' });
  }, []);

  const handleSubmitDraftProcFeeAssignment = useCallback(
    (values: DraftFirmProcFeeAssignment, mode: CrudMode) => {
      const data = {
        id: values.id,
        index: values.index,
        procurationFeeId: values.procurationFeeId,
        procurationFeeRuleId: values.procurationFeeRuleId,
        firmId: firmId,
      };

      return firmProcFeeAssignmentsCreateChangeRequest({
        body: {
          data,
          actionType: crudToActionMap[mode] as ActionType,
        },
      });
    },
    [firmId],
  );

  const handleReorder = (newOrder: FirmProcFeeAssignmentItemsResponse[]) => {
    const payload = newOrder?.map((item: FirmProcFeeAssignmentItemsResponse, index: number) => ({
      id: item?.id,
      procurationFeeId: item?.procurationFee?.key ?? undefined,
      procurationFeeRuleId: item?.procurationFeeRule?.key ?? undefined,
      index: index,
      lockedByActionId: item?.lockedByActionId,
      firmId: firmId,
    }));

    return firmProcFeeAssignmentsCreateFirmReorderChangeRequest({
      body: {
        actionType: ActionType.REORDER,
        data: payload,
      },
      path: { firmId },
    });
  };

  const initialValues: DraftFirmProcFeeAssignment | null = useMemo(() => {
    if (!state.crud.record) return null;
    const { procurationFee, procurationFeeRule, ...data } = state.crud.record;
    return {
      ...data,
      procurationFeeId: procurationFee?.key ?? undefined,
      procurationFeeRuleId: procurationFeeRule?.key ?? undefined,
      firmId,
    };
  }, [state.crud.record, firmId]);

  return (
    <TabContent noScroll>
      <CrudFormCard collapsible={readonly} icon={<IconAddressBook />} title={t('firms.labels.procurationFees')}>
        <ProcurationFeeAssignmentsList
          procurationFees={data}
          loading={isFetching}
          valueLists={valueLists}
          readonly={readonly}
          parentEntityType={ActionEntityType.FIRMS}
          columns={procurationFeeAssignmentsColumns}
          enableRowDnD
          onRowDnDEnd={handleReorder}
          onCreate={onCreate}
          onView={onView}
          onEdit={onEdit}
          onDelete={onDelete}
          refreshTable={refreshTable}
          guardFlags={['enableFirms']}
          permissionKey="firms"
        />
        {state.crud.open && state.crud.mode && (
          <ProcurationFeeAssignmentsCrudForm
            mode={state.crud.mode}
            valueLists={valueLists}
            initialValues={initialValues}
            refreshTable={refreshTable}
            onClose={onCloseCrud}
            submitChangeRequest={handleSubmitDraftProcFeeAssignment}
          />
        )}
        <ConfirmCrDeleteModal
          open={state.deleteModal.open}
          records={state.deleteModal.record ? [state.deleteModal.record] : []}
          idKey="id"
          nameKey="id"
          entityLabel={t('lender.procurationFees.labels.procurationFee')}
          entityLabelPlural={t('lender.procurationFees.labels.procurationFees')}
          firmId={firmId}
          getFirmPrecheck={firmProcFeeAssignmentsValidateDelete}
          createFirmDeleteChangeRequest={firmProcFeeAssignmentsCreateDeleteCr}
          refreshTable={refreshTable}
          onClose={onCloseDeleteModal}
        />
      </CrudFormCard>
    </TabContent>
  );
};
